/* Altara Journey · Intro sequence
 * ~12 s cinematic opener that lands directly into the Meet-Nomsa still.
 *   Beat A · "We can't control everything an investor sees." (4 s)
 *   Beat B · "But we can change what they trust."            (4 s)
 *   Beat C · Meet Nomsa · pre-cropped still                  (4 s)
 * The very first rendered frame is Beat A's text on a cinematic dark background,
 * so PowerPoint's auto-poster shows a strong opening card — never a blank frame.
 */
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

const NAVY = "#0A1F44";
const GOLD = "#C9A24A";
const CREAM = "#F5EFE6";
const MEET_BG = "/prologue/nomsa_meet_bg.png";

const BEAT_A_MS = 0;
const BEAT_B_MS = 0;
const BEAT_C_MS = 5500;

export default function AltaraJourneyIntroDemo() {
  const [params] = useSearchParams();
  const embed = params.get("embed") === "1";
  // The two hook cards were removed on 2026-02 — the intro is now just the Meet Nomsa still.
  const [beat, setBeat] = useState("C");

  useEffect(() => {
    // no beat transitions — hold on Meet Nomsa for the whole intro
  }, []);

  const scene = (
    <div className="relative w-full h-full overflow-hidden" data-testid="altara-intro"
      style={{ background: "#050914" }}>

      {/* Text beats A & B — cinematic dark cinematic canvas */}
      <AnimatePresence mode="wait">
        {beat === "A" && (
          <motion.div key="A"
            className="absolute inset-0 flex items-center justify-center"
            initial={{ opacity: 1 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.7, ease: "easeInOut" }}
            style={{ background: "radial-gradient(ellipse at 50% 50%, #142958 0%, #0A1F44 45%, #050914 100%)" }}
          >
            <TextCard eyebrow="ALTARA CONSUMER INTELLIGENCE" line1="We can't control" line2="everything an investor sees." />
          </motion.div>
        )}
        {beat === "B" && (
          <motion.div key="B"
            className="absolute inset-0 flex items-center justify-center"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.8, ease: "easeInOut" }}
            style={{ background: "radial-gradient(ellipse at 50% 50%, #142958 0%, #0A1F44 45%, #050914 100%)" }}
          >
            <TextCard eyebrow="ALTARA CONSUMER INTELLIGENCE" line1="But we can change" line2={<>what they <span style={{ color: GOLD }}>trust</span>.</>} />
          </motion.div>
        )}
        {beat === "C" && (
          <motion.div key="C"
            className="absolute inset-0"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.9, ease: "easeInOut" }}
          >
            {/* Meet Nomsa still — pre-cropped, MEET NOMSA baked in */}
            <img src={MEET_BG} alt="" className="absolute inset-0 w-full h-full"
                 style={{ objectFit: "cover" }} />
            {/* Subtle bottom vignette so nothing steals focus from the baked headline */}
            <div className="absolute inset-0"
                 style={{ background: "linear-gradient(to bottom, transparent 55%, rgba(5,9,20,0.35) 100%)" }} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );

  if (embed) {
    return (
      <div className="w-screen h-screen" style={{ background: "#050914" }}>{scene}</div>
    );
  }
  return (
    <div className="w-full" style={{ background: "#050914", minHeight: "100vh" }}>
      <div className="mx-auto" style={{ maxWidth: 1600, aspectRatio: "16/9" }}>{scene}</div>
    </div>
  );
}

function TextCard({ eyebrow, line1, line2 }) {
  return (
    <div className="text-center px-8" style={{ maxWidth: 1200 }}>
      <motion.div
        className="text-[13px] font-semibold tracking-[0.36em] mb-8"
        style={{ color: GOLD }}
        initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.15 }}
      >{eyebrow}</motion.div>
      <motion.div
        className="text-white font-semibold leading-[1.08]"
        style={{ fontSize: 76, fontFamily: "'Inter', sans-serif", letterSpacing: -0.5, textShadow: "0 4px 40px rgba(0,0,0,0.7)" }}
        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.75, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
      >
        <div>{line1}</div>
        <div>{line2}</div>
      </motion.div>
      <motion.div
        className="mx-auto mt-10"
        style={{ width: 96, height: 2, background: GOLD, opacity: 0.85 }}
        initial={{ scaleX: 0 }} animate={{ scaleX: 1 }}
        transition={{ duration: 0.8, delay: 0.75, ease: "easeOut" }}
      />
    </div>
  );
}
